// src/pages/user/CreateProject.jsx
import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  TextField,
  MenuItem,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Snackbar,
  Alert,
  Card,
  CardContent,
} from "@mui/material";
import { styled } from "@mui/system";
import { useNavigate, useParams } from "react-router-dom";
import API from "../../api";
import { removeAuth } from "../../utils/auth";

const Container = styled(Box)({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  padding: 16,
  backgroundColor: "#f5f5f5",
});
const Content = styled(Paper)({
  width: "100%",
  maxWidth: 720,
  padding: 16,
});

const steps = ["Project Details"];
const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;

export default function CreateProject() {
  const { id } = useParams(); // optional project id
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [banks, setBanks] = useState([]);
  const [plans, setPlans] = useState([]);
  const [project, setProject] = useState(null);

  const [form, setForm] = useState({
    companyName: "",
    companyAddress: "",
    email: "",
    phone: "",
    panOrGst: "",
    idNumber: "",
    projectType: "",
    productNames: "",
    bankName: "",
    branchName: "",
    acceptTnC: false,
  });

  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    async function init() {
      try {
        // load profile
        const { data: u } = await API.get("/profile");
        setForm((f) => ({
          ...f,
          email: u.email,
          phone: u.mobile || "",
        }));

        // load banks & plans
        const [banksRes, plansRes] = await Promise.all([
          API.get("/master/banks"),
          API.get("/master/plans"),
        ]);
        setBanks(banksRes.data);
        setPlans(plansRes.data);

        // if editing existing project
        if (id) {
          const { data: all } = await API.get("/user/projects");
          const existing = all.find((p) => p._id === id);
          if (!existing) {
            navigate("/user", { replace: true });
            return;
          }
          if (existing.isPaid) {
            // paid projects cannot be edited from here
            navigate("/user", { replace: true });
            return;
          }
          setProject(existing);
          setForm({
            companyName: existing.companyName,
            companyAddress: existing.companyAddress,
            email: existing.email,
            phone: existing.phone,
            panOrGst: existing.panOrGst,
            idNumber: existing.idNumber,
            projectType: existing.projectType,
            productNames: existing.productNames,
            bankName: existing.bankName,
            branchName: existing.branchName,
            acceptTnC: true, // already accepted earlier
          });
        }
      } catch (err) {
        console.error(err);
        if (err.response?.status === 401 || err.response?.status === 403) {
          removeAuth();
          navigate("/login", { replace: true });
        }
      } finally {
        setLoading(false);
      }
    }
    init();
  }, [id, navigate]);

  const show = (message, severity = "error") =>
    setSnack({ open: true, message, severity });

  const validate = () => {
    const {
      companyName,
      companyAddress,
      email,
      phone,
      panOrGst,
      idNumber,
      projectType,
      productNames,
      bankName,
      branchName,
      acceptTnC,
    } = form;
    if (!companyName || !companyAddress || !email || !phone) {
      show("Fill all contact fields", "warning");
      return false;
    }
    if (!panOrGst) {
      show("Select ID Type (PAN / GST)", "warning");
      return false;
    }
    if (panOrGst === "PAN" && !panRegex.test(idNumber)) {
      show("Invalid PAN format", "warning");
      return false;
    }
    if (panOrGst === "GST" && !gstRegex.test(idNumber)) {
      show("Invalid GST format", "warning");
      return false;
    }
    if (!projectType || !productNames) {
      show("Specify project & products", "warning");
      return false;
    }
    if (!bankName || !branchName) {
      show("Select bank & branch", "warning");
      return false;
    }
    if (!acceptTnC) {
      show("Accept Terms & Conditions", "warning");
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validate()) return;
    try {
      let saved;
      if (!project) {
        const { data } = await API.post("/user/projects", form);
        saved = data;
      } else {
        const { data } = await API.put(`/user/projects/${project._id}`, form);
        saved = data;
      }
      setProject(saved);
      show("Project details saved successfully", "success");
      // After saving, go back to dashboard / project list
      navigate("/user", { replace: true });
    } catch (err) {
      console.error(err);
      show(err.response?.data?.message || "Save failed", "error");
    }
  };

  if (loading) {
    return (
      <Box textAlign="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  const bankNames = Array.from(new Set(banks.map((b) => b.name)));
  const branchOptions = banks
    .filter((b) => b.name === form.bankName)
    .map((b) => b.branchName);

  return (
    <>
      <Container>
        <Content elevation={4}>
          <Typography variant="h6" align="center" gutterBottom>
            {project ? "Update Project" : "Create New Project"}
          </Typography>

          <Stepper activeStep={0} alternativeLabel sx={{ mb: 2 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          <Box component="form" noValidate>
            {/* Company Name + Company Address */}
            <Box display="flex" gap={1}>
              <TextField
                fullWidth
                size="small"
                margin="dense"
                label="Company Name"
                value={form.companyName}
                onChange={(e) =>
                  setForm((f) => ({ ...f, companyName: e.target.value }))
                }
              />
              <TextField
                fullWidth
                size="small"
                margin="dense"
                label="Company Address"
                value={form.companyAddress}
                onChange={(e) =>
                  setForm((f) => ({ ...f, companyAddress: e.target.value }))
                }
              />
            </Box>

            {/* Email + Phone */}
            <Box display="flex" gap={1}>
              <TextField
                fullWidth
                size="small"
                margin="dense"
                label="Email"
                value={form.email}
                onChange={(e) =>
                  setForm((f) => ({ ...f, email: e.target.value }))
                }
              />
              <TextField
                fullWidth
                size="small"
                margin="dense"
                label="Phone Number"
                value={form.phone}
                onChange={(e) =>
                  setForm((f) => ({ ...f, phone: e.target.value }))
                }
              />
            </Box>

            {/* ID Type + PAN/GST Number */}
            <Box display="flex" gap={1}>
              <TextField
                select
                fullWidth
                size="small"
                margin="dense"
                label="ID Type"
                value={form.panOrGst}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    panOrGst: e.target.value,
                    idNumber: "",
                  }))
                }
              >
                <MenuItem value="PAN">PAN</MenuItem>
                <MenuItem value="GST">GST</MenuItem>
              </TextField>
              <TextField
                fullWidth
                size="small"
                margin="dense"
                label={form.panOrGst === "PAN" ? "PAN Number" : "GST Number"}
                value={form.idNumber}
                onChange={(e) =>
                  setForm((f) => ({ ...f, idNumber: e.target.value }))
                }
              />
            </Box>

            {/* Project Type + Product Names */}
            <Box display="flex" gap={1}>
              <TextField
                fullWidth
                size="small"
                margin="dense"
                label="Project Type"
                value={form.projectType}
                onChange={(e) =>
                  setForm((f) => ({ ...f, projectType: e.target.value }))
                }
              />
              <TextField
                fullWidth
                size="small"
                margin="dense"
                label="Product Names"
                value={form.productNames}
                onChange={(e) =>
                  setForm((f) => ({ ...f, productNames: e.target.value }))
                }
              />
            </Box>

            {/* Bank + Branch */}
            <Box display="flex" gap={1}>
              <TextField
                select
                fullWidth
                size="small"
                margin="dense"
                label="Bank"
                value={form.bankName}
                onChange={(e) =>
                  setForm((f) => ({ ...f, bankName: e.target.value }))
                }
              >
                {bankNames.map((name) => (
                  <MenuItem key={name} value={name}>
                    {name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                fullWidth
                size="small"
                margin="dense"
                label="Branch"
                value={form.branchName}
                onChange={(e) =>
                  setForm((f) => ({ ...f, branchName: e.target.value }))
                }
              >
                {branchOptions.map((br) => (
                  <MenuItem key={br} value={br}>
                    {br}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            {/* Plan cards were intentionally commented out by you, leaving as-is */}
            {/* <Box display="flex" justifyContent="space-between" mt={1}>
              {plans.map((p) => (
                <Card key={p._id} variant="outlined" sx={{ flex: 1, mx: 0.5 }}>
                  <CardContent sx={{ py: 1, textAlign: "center" }}>
                    <Typography variant="subtitle2">{p.name}</Typography>
                    <Typography variant="body2">₹{p.price}</Typography>
                    <Typography variant="caption" display="block">
                      Range: {p.minCost}–{p.maxCost} lacs
                    </Typography>
                  </CardContent>
                </Card>
              ))}
            </Box> */}

            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={form.acceptTnC}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, acceptTnC: e.target.checked }))
                  }
                />
              }
              label="I accept the Terms & Conditions"
              sx={{ mt: 1 }}
            />
          </Box>

          <Box display="flex" justifyContent="space-between" mt={2}>
            <Button onClick={() => navigate("/user", { replace: true })}>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleSave}>
              Save
            </Button>
          </Box>
        </Content>
      </Container>

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snack.severity}>{snack.message}</Alert>
      </Snackbar>
    </>
  );
}
